import React, { useCallback, useEffect, useState } from 'react';
import axios from '../api/axiosConfig';
import * as XLSX from 'xlsx';
import Pagination from './Pagination';

// Pestaña "Pagados por fuera" del módulo Pagos a Tránsitos: historial de trámites que ya se le pagaron
// al tránsito antes del módulo y que, por eso, no entran en los lotes.
const inputStyle = {
  padding: '10px 14px', border: '1px solid var(--input-border)', borderRadius: '10px',
  background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '13px', fontWeight: '500', outline: 'none', width: '100%'
};
const labelStyle = { fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase' };
const btn = (bg) => ({ padding: '9px 16px', background: bg, color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' });
const smallBtn = (bg) => ({ ...btn(bg), padding: '6px 12px', fontSize: '12px', borderRadius: '8px' });
const money = (v) => (v === null || v === undefined) ? '—' : `$ ${Number(v).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
const fFechaHora = (d) => d ? new Date(d).toLocaleString('es-CO', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fFecha = (d) => d ? new Date(d).toLocaleDateString('es-CO', { timeZone: 'UTC' }) : '—';

const PagadosFueraTab = ({ transitos, isSuperadmin, refreshKey, flash, fail }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fTransito, setFTransito] = useState('todos');
  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');
  const [fBuscar, setFBuscar] = useState('');
  const [verRevertidos, setVerRevertidos] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [revertir, setRevertir] = useState(null); // { row, motivo }
  const [busy, setBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const p = new URLSearchParams();
      if (fTransito !== 'todos') p.append('transito_id', fTransito);
      if (fDesde) p.append('fecha_inicio', fDesde);
      if (fHasta) p.append('fecha_fin', fHasta);
      if (fBuscar.trim()) p.append('search', fBuscar.trim());
      if (verRevertidos) p.append('incluir_revertidos', 'true');
      const r = await axios.get(`/api/pagos-transito/pagados-fuera?${p.toString()}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      fail(e, 'Error al cargar los pagados por fuera');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fTransito, fDesde, fHasta, fBuscar, verRevertidos]);

  useEffect(() => { const t = setTimeout(fetchRows, 300); return () => clearTimeout(t); }, [fetchRows, refreshKey]);

  const total = rows.filter(r => !r.revertido).reduce((s, r) => s + (Number(r.valor) || 0), 0);
  const current = rows.slice((page - 1) * perPage, page * perPage);

  const exportar = () => {
    if (rows.length === 0) { alert('No hay registros para exportar.'); return; }
    const data = rows.map(r => ({
      'Trámite': r.tramite_id, 'Placa': r.placa || '—', 'Tipo de Trámite': r.tipo_tramite || '—', 'Concesionario': r.concesionario || '—',
      'Propietario': r.propietario || '—', 'Tránsito': r.transito_name, 'Fecha Ejecución': fFechaHora(r.fecha_ejecucion),
      'Valor (tarifa)': r.valor === null ? '' : Number(r.valor), 'Motivo': r.motivo, 'Sacado del lote': r.lote_referencia || '', 'Referencia / soporte externo': r.referencia || '',
      'Fecha de pago externo': r.fecha_pago ? fFecha(r.fecha_pago) : '', 'Marcado por': r.marcado_by_username, 'Fecha de marcado': fFechaHora(r.marcado_at),
      'Estado': r.revertido ? 'Revertido' : 'Pagado por fuera', 'Motivo reversión': r.motivo_reversion || ''
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pagados por fuera');
    XLSX.writeFile(wb, `Pagados_por_fuera_${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' })}.xlsx`);
  };

  const confirmarRevertir = async () => {
    if (!revertir.motivo.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`/api/pagos-transito/pagados-fuera/${revertir.row.id}/revertir`, { motivo: revertir.motivo });
      flash(r.data.message);
      setRevertir(null);
      fetchRows();
    } catch (e) {
      fail(e, 'Error al revertir');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{ padding: '14px 18px', borderRadius: '12px', background: '#ecfdf5', color: '#065f46', marginBottom: '16px', fontSize: '13px' }}>
        <strong>Pagados por fuera:</strong> trámites que ya se le pagaron al tránsito antes del módulo. No entran en los lotes.
        Para marcarlos, use <em>Liquidar pago a tránsito</em>, seleccione los trámites y pulse "Marcar como pagados por fuera".
        <div style={{ marginTop: '6px', fontWeight: 700 }}>{rows.filter(r => !r.revertido).length} trámite(s) · {money(total)} (según tarifa)</div>
      </div>

      <div className="dashboard-filters" style={{
        background: 'var(--card-bg)', padding: '16px 24px', borderRadius: '14px', marginBottom: '20px',
        boxShadow: 'var(--card-shadow)', border: '1px solid var(--card-border)',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', alignItems: 'end'
      }}>
        {transitos.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="pf-tr" style={labelStyle}>Tránsito</label>
            <select id="pf-tr" value={fTransito} onChange={e => { setFTransito(e.target.value); setPage(1); }} style={inputStyle}>
              <option value="todos">Todos</option>
              {transitos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label htmlFor="pf-de" style={labelStyle}>Ejecución desde</label>
          <input id="pf-de" type="date" value={fDesde} onChange={e => { setFDesde(e.target.value); setPage(1); }} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label htmlFor="pf-ha" style={labelStyle}>Ejecución hasta</label>
          <input id="pf-ha" type="date" value={fHasta} onChange={e => { setFHasta(e.target.value); setPage(1); }} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label htmlFor="pf-bu" style={labelStyle}>Buscar</label>
          <input id="pf-bu" type="text" placeholder="Placa, trámite, motivo..." value={fBuscar} onChange={e => { setFBuscar(e.target.value); setPage(1); }} style={inputStyle} />
        </div>
        <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
          <input type="checkbox" checked={verRevertidos} onChange={e => { setVerRevertidos(e.target.checked); setPage(1); }} /> Ver revertidos
        </label>
        <button type="button" onClick={exportar} style={btn('#10b981')}>📥 Exportar</button>
      </div>

      {loading ? <p className="loading">Cargando...</p> : (
        <div className="table-wrapper">
          <div className="table-responsive">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Trámite</th><th>Placa</th><th>Tipo de trámite</th><th>Concesionario</th><th>Tránsito</th>
                  <th>Ejecutado</th><th>Valor</th><th>Motivo</th><th>Marcado por</th><th>Estado</th>{isSuperadmin && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {current.map(r => (
                  <tr key={r.id} style={r.revertido ? { opacity: 0.55 } : undefined}>
                    <td data-label="Trámite">#{r.tramite_id}</td>
                    <td data-label="Placa"><strong>{r.placa || '—'}</strong></td>
                    <td data-label="Tipo de trámite">{r.tipo_tramite || '—'}</td>
                    <td data-label="Concesionario">{r.concesionario || '—'}</td>
                    <td data-label="Tránsito">{r.transito_name}</td>
                    <td data-label="Ejecutado" style={{ fontSize: '12px' }}>{fFechaHora(r.fecha_ejecucion)}</td>
                    <td data-label="Valor">{money(r.valor)}</td>
                    <td data-label="Motivo" style={{ fontSize: '12px', maxWidth: '260px' }}>
                      {r.motivo}
                      {r.lote_referencia && <div style={{ color: '#b45309' }}>Sacado del lote {r.lote_referencia}</div>}
                      {(r.referencia || r.fecha_pago) && <div style={{ color: '#64748b' }}>{r.referencia ? `Ref: ${r.referencia}` : ''}{r.fecha_pago ? ` · Pagado el ${fFecha(r.fecha_pago)}` : ''}</div>}
                    </td>
                    <td data-label="Marcado por" style={{ fontSize: '12px' }}>{r.marcado_by_username}<br /><span style={{ color: '#64748b' }}>{fFechaHora(r.marcado_at)}</span></td>
                    <td data-label="Estado">
                      {r.revertido
                        ? <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: '#64748b', background: '#f1f5f9' }} title={r.motivo_reversion}>Revertido</span>
                        : <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: '#15803d', background: '#dcfce7' }}>Pagado por fuera</span>}
                    </td>
                    {isSuperadmin && (
                      <td data-label="Acciones">
                        {!r.revertido && <button type="button" onClick={() => setRevertir({ row: r, motivo: '' })} style={smallBtn('#dc2626')}>Revertir</button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No hay trámites marcados como pagados por fuera.</div>}
          <Pagination currentPage={page} totalItems={rows.length} itemsPerPage={perPage} onPageChange={setPage}
            onItemsPerPageChange={(n) => { setPerPage(n); setPage(1); }} />
        </div>
      )}

      {revertir && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Revertir pagado por fuera">
          <div className="modal-content" style={{ maxWidth: '520px', width: '95%' }}>
            <h3 style={{ marginTop: 0 }}>Revertir trámite #{revertir.row.tramite_id} ({revertir.row.placa})</h3>
            <p style={{ fontSize: '13px', color: '#64748b' }}>El trámite volverá a aparecer como candidato para un lote de pago. El registro queda en el historial como revertido.</p>
            <label htmlFor="pf-rev" style={labelStyle}>Motivo</label>
            <textarea id="pf-rev" rows={3} value={revertir.motivo} style={inputStyle} onChange={e => setRevertir(v => ({ ...v, motivo: e.target.value }))} />
            <div className="modal-actions" style={{ marginTop: '16px' }}>
              <button type="button" className="btn-secondary" onClick={() => setRevertir(null)}>Cancelar</button>
              <button type="button" style={btn('#dc2626')} disabled={busy || !revertir.motivo.trim()} onClick={confirmarRevertir}>{busy ? 'Revirtiendo...' : 'Revertir'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PagadosFueraTab;
